import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AlertTriangle,
  ChevronRight,
  ChevronDown,
  Filter,
  AlertOctagon,
  Shield,
  HelpCircle,
  CheckCircle,
  Users,
  FileWarning,
  XCircle,
  Eye,
  RefreshCw,
} from 'lucide-react';
import { api } from '../services/api';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Select } from '../components/ui/Select';
import { Modal, ModalFooter } from '../components/ui/Modal';
import { Alert } from '../components/ui/Alert';
import { Spinner } from '../components/ui/Spinner';
import { EmptyState } from '../components/ui/EmptyState';
import { Pagination } from '../components/ui/Pagination';
import { Input } from '../components/ui/Input';

export default function ConflictsPage() {
  const [severity, setSeverity] = useState<string>('');
  const [page, setPage] = useState(1);
  const [expandedConflict, setExpandedConflict] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [confirmingEntityId, setConfirmingEntityId] = useState<number | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['conflicts', { severity, page }],
    queryFn: () => api.getConflicts({ severity: severity || undefined, page, limit: 20 }),
  });

  // Fetch entity details for expanded conflict
  const { data: entityData, isLoading: entityLoading } = useQuery({
    queryKey: ['entity-detail', expandedConflict],
    queryFn: () => api.getEntity(parseInt(expandedConflict!, 10)),
    enabled: !!expandedConflict,
  });

  // Fetch "Why" analysis for expanded conflict
  const { data: whyData, isLoading: whyLoading } = useQuery({
    queryKey: ['entity-why', expandedConflict],
    queryFn: () => api.getEntityWhy(parseInt(expandedConflict!, 10)),
    enabled: !!expandedConflict,
  });

  // Get the current expanded conflict data
  const expandedConflictData = data?.conflicts?.find(c => String(c.entityId) === expandedConflict);

  // Count DAP alerts (high severity unresolved)
  const dapAlertCount = data?.conflicts?.filter(c => c.isDapAlert && !c.isResolved).length || 0;

  // Mutation: Confirm patron is on exclusion list (set IS_DAP_FLAG = '01')
  const confirmDapMutation = useMutation({
    mutationFn: (entityId: number) => api.confirmDap(entityId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conflicts'] });
      queryClient.invalidateQueries({ queryKey: ['entity-detail'] });
      setConfirmingEntityId(null);
      setExpandedConflict(null);
      refetch();
    },
  });

  // Get the conflict being confirmed
  const confirmingConflict = data?.conflicts?.find(c => c.entityId === confirmingEntityId);

  // Mutation: Resolve conflict as false positive
  const resolveConflictMutation = useMutation({
    mutationFn: ({ entityId, notes }: { entityId: number; notes?: string }) =>
      api.resolveConflict(entityId, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conflicts'] });
      setExpandedConflict(null);
      setRejectReason('');
      refetch();
    },
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Regulatory Conflicts</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Review and resolve entity matches with exclusion lists
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />}
          onClick={() => refetch()}
        >
          Refresh
        </Button>
      </div>

      {/* DAP Alert Banner */}
      <AnimatePresence>
        {dapAlertCount > 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
          >
            <div className="bg-gradient-to-r from-red-600 to-red-700 dark:from-red-700 dark:to-red-800 text-white rounded-xl p-5 shadow-lg">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/10 rounded-xl">
                  <AlertOctagon className="h-8 w-8" />
                </div>
                <div className="flex-1">
                  <h2 className="text-lg font-bold">
                    DAP Alert: {dapAlertCount} High-Priority Conflict{dapAlertCount > 1 ? 's' : ''}
                  </h2>
                  <p className="text-red-100 text-sm mt-1">
                    Loyalty patrons have been matched with exclusion list records. Immediate review required.
                  </p>
                </div>
                <Shield className="h-12 w-12 text-white/20" />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Summary Stats */}
      {data?.summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total Conflicts" value={data.summary.totalConflicts} variant="default" />
          <StatCard label="High Severity" value={data.summary.highSeverity} variant="danger" />
          <StatCard label="Medium Severity" value={data.summary.mediumSeverity} variant="warning" />
          <StatCard label="Low Severity" value={data.summary.lowSeverity} variant="success" />
        </div>
      )}

      {/* Filters */}
      <Card padding="none">
        <CardContent className="!mt-0 p-4">
          <div className="flex items-center gap-4">
            <Filter className="h-5 w-5 text-gray-400 dark:text-gray-500" />
            <Select
              value={severity}
              onChange={(e) => { setSeverity(e.target.value); setPage(1); }}
              options={[
                { value: '', label: 'All Severities' },
                { value: 'HIGH', label: 'High' },
                { value: 'MEDIUM', label: 'Medium' },
                { value: 'LOW', label: 'Low' },
              ]}
              className="w-48"
            />
          </div>
        </CardContent>
      </Card>

      {/* Conflicts List */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : error ? (
        <Alert variant="danger" title="Error loading conflicts">
          Failed to fetch conflict data. Please try again.
        </Alert>
      ) : data?.conflicts && data.conflicts.length > 0 ? (
        <Card padding="none">
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {data.conflicts.map((conflict) => {
              const isExpanded = expandedConflict === String(conflict.entityId);

              return (
                <motion.div
                  key={conflict.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className={`${conflict.isDapAlert ? 'border-l-4 border-red-500' : ''}`}
                >
                  <div className="p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <button
                          onClick={() => setExpandedConflict(isExpanded ? null : String(conflict.entityId))}
                          className={`p-2 rounded-xl transition-all ${getSeverityBgClass(conflict.severity)} hover:opacity-80`}
                        >
                          {isExpanded ? (
                            <ChevronDown className={`h-5 w-5 ${getSeverityTextClass(conflict.severity)}`} />
                          ) : (
                            <AlertTriangle className={`h-5 w-5 ${getSeverityTextClass(conflict.severity)}`} />
                          )}
                        </button>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold text-gray-900 dark:text-gray-100">
                              {conflict.resolvedName || `Entity ${conflict.entityId}`}
                            </p>
                            {conflict.isDapAlert && (
                              <Badge variant="danger" size="sm">DAP</Badge>
                            )}
                          </div>
                          <div className="flex gap-4 mt-1 text-sm text-gray-500 dark:text-gray-400">
                            <span>{conflict.loyaltyRecordCount} loyalty records</span>
                            <span>{conflict.exclusionRecordCount} exclusion records</span>
                            <span>Confidence: {conflict.matchConfidence}%</span>
                          </div>
                          {conflict.exclusionReason && (
                            <div className="mt-2 text-sm">
                              <span className="text-red-600 dark:text-red-400 font-medium">Exclusion Reason: </span>
                              <span className="text-gray-700 dark:text-gray-300">{conflict.exclusionReason}</span>
                              {conflict.exclusionDate && (
                                <span className="text-gray-500 dark:text-gray-400 ml-2">({conflict.exclusionDate})</span>
                              )}
                            </div>
                          )}
                          <div className="flex gap-2 mt-2 flex-wrap">
                            <SeverityBadge severity={conflict.severity} />
                            {conflict.jurisdictions.map((j) => (
                              <Badge key={j} variant="default" size="sm">{j}</Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          leftIcon={<HelpCircle className="h-4 w-4" />}
                          onClick={() => setExpandedConflict(isExpanded ? null : String(conflict.entityId))}
                        >
                          Why
                        </Button>
                        <Link to={`/entities/${conflict.entityId}`}>
                          <Button variant="ghost" size="sm" rightIcon={<ChevronRight className="h-4 w-4" />}>
                            View
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>

                  {/* Expanded "Why" Analysis */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="px-4 pb-4 bg-blue-50 dark:bg-blue-900/20 border-t border-blue-100 dark:border-blue-800"
                      >
                        <div className="mt-4">
                          <div className="flex items-center gap-2 mb-4">
                            <HelpCircle className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                            <h4 className="font-semibold text-blue-900 dark:text-blue-200">Why These Records Match</h4>
                          </div>

                          {entityLoading || whyLoading ? (
                            <div className="flex justify-center py-6">
                              <Spinner />
                            </div>
                          ) : entityData ? (
                            <div className="space-y-4">
                              {/* Match Key Info */}
                              {whyData?.whyResults && whyData.whyResults.length > 0 && whyData.whyResults[0]?.matchInfo?.whyKey && (
                                <div className="bg-white dark:bg-gray-800 rounded-xl border border-blue-200 dark:border-blue-700 p-4">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-sm text-gray-500 dark:text-gray-400">Match Key:</span>
                                    <span className="font-mono text-sm bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 px-2 py-0.5 rounded">
                                      {whyData.whyResults[0].matchInfo.whyKey}
                                    </span>
                                    {whyData.whyResults[0].matchInfo.featureScores && Object.keys(whyData.whyResults[0].matchInfo.featureScores).length > 0 && (
                                      <div className="flex gap-2 flex-wrap mt-2 sm:mt-0">
                                        {Object.entries(whyData.whyResults[0].matchInfo.featureScores).map(([feature, scoreData]) => (
                                          <Badge
                                            key={feature}
                                            variant={scoreData.score >= 90 ? 'success' : scoreData.score >= 70 ? 'warning' : 'danger'}
                                            size="sm"
                                          >
                                            {feature}: {scoreData.score}%
                                          </Badge>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Side-by-side record comparison */}
                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                {/* Loyalty Records */}
                                <div className="bg-white dark:bg-gray-800 rounded-xl border border-blue-300 dark:border-blue-700 overflow-hidden">
                                  <div className="px-4 py-3 bg-blue-100 dark:bg-blue-900/50 border-b border-blue-300 dark:border-blue-700 flex items-center gap-2">
                                    <Users className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                                    <span className="font-medium text-blue-800 dark:text-blue-200">Loyalty Records</span>
                                    <Badge variant="info" size="sm" className="ml-auto">
                                      {entityData.linkedRecords?.filter(r => expandedConflictData?.loyaltyDataSources?.includes(r.dataSource)).length || 0}
                                    </Badge>
                                  </div>
                                  <div className="p-4 space-y-3 max-h-64 overflow-y-auto">
                                    {entityData.linkedRecords?.filter(r => expandedConflictData?.loyaltyDataSources?.includes(r.dataSource)).map((record, idx) => {
                                      const raw = record.rawData || {};
                                      const firstName = raw.First_Name || raw['FIRST NAME'] || '';
                                      const lastName = raw.Last_Name || raw['LAST NAME'] || '';
                                      const dob = raw.DOB || raw.DATE_OF_BIRTH || '';
                                      const address = raw.Address_Line_1 || raw.ADDR_FULL || '';
                                      const city = raw.City || raw.ADDR_CITY || '';
                                      const state = raw.State || raw.ADDR_STATE || '';
                                      const loyaltyId = raw.Universal_Player_ID || raw.LOYALTY_ID || '';

                                      return (
                                        <div key={idx} className="text-sm border-b border-gray-100 dark:border-gray-700 pb-3 last:border-0 last:pb-0">
                                          <div className="flex items-center gap-2 mb-2">
                                            <Badge variant="info" size="sm">{record.dataSource}</Badge>
                                            <span className="text-gray-400 dark:text-gray-500 text-xs font-mono">{record.recordId}</span>
                                          </div>
                                          <div className="space-y-1 text-gray-700 dark:text-gray-300">
                                            {!!(firstName || lastName) && (
                                              <div className="flex"><span className="text-gray-500 dark:text-gray-400 w-28 shrink-0">Name:</span><span className="font-medium">{[firstName, lastName].filter(Boolean).map(String).join(' ')}</span></div>
                                            )}
                                            {!!dob && (
                                              <div className="flex"><span className="text-gray-500 dark:text-gray-400 w-28 shrink-0">DOB:</span><span className="font-medium">{String(dob)}</span></div>
                                            )}
                                            {!!address && (
                                              <div className="flex"><span className="text-gray-500 dark:text-gray-400 w-28 shrink-0">Address:</span><span className="font-medium">{String(address)}</span></div>
                                            )}
                                            {!!(city || state) && (
                                              <div className="flex"><span className="text-gray-500 dark:text-gray-400 w-28 shrink-0">City/State:</span><span className="font-medium">{[city, state].filter(Boolean).map(String).join(', ')}</span></div>
                                            )}
                                            {!!loyaltyId && (
                                              <div className="flex"><span className="text-gray-500 dark:text-gray-400 w-28 shrink-0">Loyalty ID:</span><span className="font-mono font-medium text-blue-600 dark:text-blue-400">{String(loyaltyId)}</span></div>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                    {entityData.linkedRecords?.filter(r => expandedConflictData?.loyaltyDataSources?.includes(r.dataSource)).length === 0 && (
                                      <p className="text-gray-500 dark:text-gray-400 text-sm">No loyalty records found</p>
                                    )}
                                  </div>
                                </div>

                                {/* Exclusion Records */}
                                <div className="bg-white dark:bg-gray-800 rounded-xl border border-red-300 dark:border-red-700 overflow-hidden">
                                  <div className="px-4 py-3 bg-red-100 dark:bg-red-900/50 border-b border-red-300 dark:border-red-700 flex items-center gap-2">
                                    <FileWarning className="h-4 w-4 text-red-600 dark:text-red-400" />
                                    <span className="font-medium text-red-800 dark:text-red-200">Exclusion Records</span>
                                    <Badge variant="danger" size="sm" className="ml-auto">
                                      {entityData.linkedRecords?.filter(r => expandedConflictData?.exclusionDataSources?.includes(r.dataSource)).length || 0}
                                    </Badge>
                                  </div>
                                  <div className="p-4 space-y-3 max-h-64 overflow-y-auto">
                                    {entityData.linkedRecords?.filter(r => expandedConflictData?.exclusionDataSources?.includes(r.dataSource)).map((record, idx) => {
                                      const raw = record.rawData || {};
                                      const firstName = raw['FIRST NAME'] || raw.First_Name || '';
                                      const lastName = raw['LAST NAME'] || raw.Last_Name || '';
                                      const dob = raw.DOB || raw['DATE OF BIRTH'] || '';
                                      const address = raw['LAST KNOWN ADDRESS'] || raw.Address_Line_1 || '';
                                      const exclusionDate = raw['DATE OF EXCLUSION'] || raw.Exclusion_Date || '';
                                      const ngcNumber = raw['NGC#'] || '';

                                      return (
                                        <div key={idx} className="text-sm border-b border-gray-100 dark:border-gray-700 pb-3 last:border-0 last:pb-0">
                                          <div className="flex items-center gap-2 mb-2">
                                            <Badge variant="danger" size="sm">{record.dataSource}</Badge>
                                            <span className="text-gray-400 dark:text-gray-500 text-xs font-mono">{record.recordId}</span>
                                          </div>
                                          <div className="space-y-1 text-gray-700 dark:text-gray-300">
                                            {!!(firstName || lastName) && (
                                              <div className="flex"><span className="text-gray-500 dark:text-gray-400 w-28 shrink-0">Name:</span><span className="font-medium">{[firstName, lastName].filter(Boolean).map(String).join(' ')}</span></div>
                                            )}
                                            {!!dob && (
                                              <div className="flex"><span className="text-gray-500 dark:text-gray-400 w-28 shrink-0">DOB:</span><span className="font-medium">{String(dob)}</span></div>
                                            )}
                                            {!!address && (
                                              <div className="flex"><span className="text-gray-500 dark:text-gray-400 w-28 shrink-0">Last Address:</span><span className="font-medium">{String(address)}</span></div>
                                            )}
                                            {!!exclusionDate && (
                                              <div className="flex"><span className="text-gray-500 dark:text-gray-400 w-28 shrink-0">Excluded:</span><span className="font-medium text-red-600 dark:text-red-400">{String(exclusionDate)}</span></div>
                                            )}
                                            {!!ngcNumber && (
                                              <div className="flex"><span className="text-gray-500 dark:text-gray-400 w-28 shrink-0">NGC#:</span><span className="font-mono font-medium text-red-700 dark:text-red-400">{String(ngcNumber)}</span></div>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                    {entityData.linkedRecords?.filter(r => expandedConflictData?.exclusionDataSources?.includes(r.dataSource)).length === 0 && (
                                      <p className="text-gray-500 dark:text-gray-400 text-sm">No exclusion records found</p>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Confidence summary */}
                              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                                <div className="flex items-center gap-4 text-sm">
                                  <div className="flex items-center gap-2">
                                    <AlertTriangle className={`h-4 w-4 ${
                                      (expandedConflictData?.matchConfidence || 0) >= 85 ? 'text-red-500' :
                                      (expandedConflictData?.matchConfidence || 0) >= 70 ? 'text-yellow-500' : 'text-green-500'
                                    }`} />
                                    <span className="text-gray-600 dark:text-gray-400">Match Confidence:</span>
                                    <span className={`font-bold ${
                                      (expandedConflictData?.matchConfidence || 0) >= 85 ? 'text-red-600 dark:text-red-400' :
                                      (expandedConflictData?.matchConfidence || 0) >= 70 ? 'text-yellow-600 dark:text-yellow-400' : 'text-green-600 dark:text-green-400'
                                    }`}>{expandedConflictData?.matchConfidence || 'N/A'}%</span>
                                  </div>
                                  <span className="text-gray-300 dark:text-gray-600">|</span>
                                  <span className="text-gray-600 dark:text-gray-400">
                                    Entity resolved these records as the <span className="font-medium">same person</span>
                                  </span>
                                </div>
                              </div>
                            </div>
                          ) : null}

                          {/* Action Buttons */}
                          {!conflict.isResolved ? (
                            <div className="mt-6 space-y-4">
                              <div className="flex flex-wrap items-center gap-3">
                                <Button
                                  variant="danger"
                                  leftIcon={<CheckCircle className="h-4 w-4" />}
                                  onClick={() => setConfirmingEntityId(conflict.entityId)}
                                  isLoading={confirmDapMutation.isPending}
                                >
                                  Confirm Exclusion Match
                                </Button>
                                <Link to={`/entities/${conflict.entityId}`}>
                                  <Button variant="secondary" leftIcon={<Eye className="h-4 w-4" />}>
                                    View Full Entity Details
                                  </Button>
                                </Link>
                              </div>

                              {/* Reject as false positive */}
                              <div className="flex items-center gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                                <div className="flex-1">
                                  <Input
                                    value={rejectReason}
                                    onChange={(e) => setRejectReason(e.target.value)}
                                    placeholder="Reason for rejection (e.g., different person, wrong DOB)..."
                                  />
                                </div>
                                <Button
                                  variant="secondary"
                                  leftIcon={<XCircle className="h-4 w-4" />}
                                  onClick={() => resolveConflictMutation.mutate({
                                    entityId: conflict.entityId,
                                    notes: rejectReason || 'False positive'
                                  })}
                                  isLoading={resolveConflictMutation.isPending}
                                >
                                  Reject (False Positive)
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <Alert variant="success" className="mt-4">
                              <div className="flex items-center gap-2">
                                <CheckCircle className="h-4 w-4" />
                                <span>This conflict has been resolved.</span>
                                {conflict.resolutionNotes && (
                                  <span className="text-gray-500 dark:text-gray-400 ml-2">Notes: {conflict.resolutionNotes}</span>
                                )}
                              </div>
                            </Alert>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        </Card>
      ) : (
        <Card>
          <EmptyState
            icon={<AlertTriangle className="h-8 w-8 text-gray-400" />}
            title="No regulatory conflicts detected"
            description="Conflicts appear when entities match records in both loyalty and exclusion lists."
          />
        </Card>
      )}

      {/* Pagination */}
      {data?.pagination && (data.pagination.totalPages ?? 0) > 1 && (
        <div className="flex justify-center">
          <Pagination
            currentPage={page}
            totalPages={data.pagination.totalPages ?? 1}
            onPageChange={setPage}
          />
        </div>
      )}

      {/* Confirmation Modal */}
      <Modal
        isOpen={confirmingEntityId !== null}
        onClose={() => setConfirmingEntityId(null)}
        title="Confirm Exclusion"
        size="md"
      >
        <div className="flex items-start gap-3 mb-4">
          <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
            <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
          </div>
          <div>
            <p className="text-gray-900 dark:text-gray-100 font-medium">
              Are you sure you want to confirm this exclusion match?
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              This will mark <span className="font-semibold">{confirmingConflict?.resolvedName || `Entity ${confirmingEntityId}`}</span> as a confirmed DAP (Denied Access Program) member.
            </p>
          </div>
        </div>

        <Alert variant="warning" className="mb-4">
          <p className="font-medium">Before confirming, make sure you have:</p>
          <ul className="mt-2 space-y-1 text-sm list-disc list-inside">
            <li>Reviewed the loyalty and exclusion records</li>
            <li>Verified this is the same person</li>
            <li>Taken appropriate action (e.g., notified security, flagged account)</li>
          </ul>
        </Alert>

        <p className="text-xs text-gray-500 dark:text-gray-400">
          This action will remove the conflict from the queue and set IS_DAP_FLAG to confirmed.
        </p>

        <ModalFooter>
          <Button variant="ghost" onClick={() => setConfirmingEntityId(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            leftIcon={<CheckCircle className="h-4 w-4" />}
            onClick={() => confirmingEntityId && confirmDapMutation.mutate(confirmingEntityId)}
            isLoading={confirmDapMutation.isPending}
          >
            Yes, Confirm Exclusion
          </Button>
        </ModalFooter>
      </Modal>
    </motion.div>
  );
}

function StatCard({ label, value, variant }: { label: string; value: number; variant: 'default' | 'danger' | 'warning' | 'success' }) {
  const variants = {
    default: 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700',
    danger: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
    warning: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800',
    success: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800',
  };

  return (
    <div className={`p-4 rounded-xl border ${variants[variant]}`}>
      <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">{value}</p>
    </div>
  );
}

function SeverityBadge({ severity }: { severity: string }) {
  const variants: Record<string, 'danger' | 'warning' | 'success' | 'default'> = {
    HIGH: 'danger',
    MEDIUM: 'warning',
    LOW: 'success',
  };
  return <Badge variant={variants[severity] || 'default'}>{severity}</Badge>;
}

function getSeverityBgClass(severity: string): string {
  const classes: Record<string, string> = {
    HIGH: 'bg-red-100 dark:bg-red-900/30',
    MEDIUM: 'bg-amber-100 dark:bg-amber-900/30',
    LOW: 'bg-green-100 dark:bg-green-900/30',
  };
  return classes[severity] || 'bg-gray-100 dark:bg-gray-800';
}

function getSeverityTextClass(severity: string): string {
  const classes: Record<string, string> = {
    HIGH: 'text-red-600 dark:text-red-400',
    MEDIUM: 'text-amber-600 dark:text-amber-400',
    LOW: 'text-green-600 dark:text-green-400',
  };
  return classes[severity] || 'text-gray-600 dark:text-gray-400';
}
